import React from "react";
import ReactDOM from "react-dom";
import useReveal from "./useReveal";
import thesisAlabImg from "../assets/thesis-alab.png";
import queueMeImg from "../assets/QueueMe.png";

function Projects() {
  const [sectionRef, sectionInView] = useReveal();

  // Helper for horizontal scroll with animation
  React.useEffect(() => {
    const scrollContainers = document.querySelectorAll(".scroll-x-animate");
    let rafId;
    function animateScroll() {
      scrollContainers.forEach((container) => {
        if (container.scrollWidth > container.clientWidth) {
          container.scrollLeft += 0.5;
          if (
            container.scrollLeft + container.clientWidth >=
            container.scrollWidth
          ) {
            container.scrollLeft = 0;
          }
        }
      });
      rafId = requestAnimationFrame(animateScroll);
    }
    animateScroll();
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Hide scrollbar with CSS
  React.useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .scroll-x-animate {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE 10+ */
      }
      .scroll-x-animate::-webkit-scrollbar {
        display: none; /* Chrome/Safari/Webkit */
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Make animation seamless and allow user scroll only for Video Works
  React.useEffect(() => {
    const scrollContainers = document.querySelectorAll(
      ".scroll-x-animate.video-works",
    );
    const speeds = new Map();

    function onUserScroll(e) {
      speeds.set(e.currentTarget, 0);
      clearTimeout(e.currentTarget._scrollTimeout);
      e.currentTarget._scrollTimeout = setTimeout(() => {
        speeds.set(e.currentTarget, 0.5);
      }, 1000);
    }

    scrollContainers.forEach((container) => {
      speeds.set(container, 0.5);
      container.addEventListener("wheel", onUserScroll, { passive: true });
      container.addEventListener("touchmove", onUserScroll, { passive: true });
    });

    let rafId;
    function animateScroll() {
      scrollContainers.forEach((container) => {
        const speed = speeds.get(container) || 0.5;
        if (container.scrollWidth > container.clientWidth && speed > 0) {
          container.scrollLeft += speed;
          if (
            container.scrollLeft + container.clientWidth >=
            container.scrollWidth - 1
          ) {
            container.scrollLeft = 0;
          }
        }
      });
      rafId = requestAnimationFrame(animateScroll);
    }
    animateScroll();

    return () => {
      scrollContainers.forEach((container) => {
        container.removeEventListener("wheel", onUserScroll);
        container.removeEventListener("touchmove", onUserScroll);
      });
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Helper to duplicate children for seamless scroll
  function duplicateChildren(children, times = 2) {
    let arr = [];
    for (let i = 0; i < times; i++) arr = arr.concat(children);
    return arr;
  }

  // Helper for drag-to-scroll
  React.useEffect(() => {
    function enableDragScroll(container) {
      let isDown = false;
      let startX;
      let scrollLeft;

      function onMouseDown(e) {
        isDown = true;
        container.classList.add("dragging");
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        document.body.style.userSelect = "none";
      }
      function onMouseLeave() {
        isDown = false;
        container.classList.remove("dragging");
        document.body.style.userSelect = "";
      }
      function onMouseUp() {
        isDown = false;
        container.classList.remove("dragging");
        document.body.style.userSelect = "";
      }
      function onMouseMove(e) {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = x - startX;
        container.scrollLeft = scrollLeft - walk;
      }

      container.addEventListener("mousedown", onMouseDown);
      container.addEventListener("mouseleave", onMouseLeave);
      container.addEventListener("mouseup", onMouseUp);
      container.addEventListener("mousemove", onMouseMove);

      // Touch events for mobile
      let touchStartX = 0;
      let touchScrollLeft = 0;
      function onTouchStart(e) {
        isDown = true;
        touchStartX = e.touches[0].pageX;
        touchScrollLeft = container.scrollLeft;
      }
      function onTouchEnd() {
        isDown = false;
      }
      function onTouchMove(e) {
        if (!isDown) return;
        const x = e.touches[0].pageX;
        const walk = x - touchStartX;
        container.scrollLeft = touchScrollLeft - walk;
      }
      container.addEventListener("touchstart", onTouchStart, { passive: true });
      container.addEventListener("touchend", onTouchEnd, { passive: true });
      container.addEventListener("touchmove", onTouchMove, { passive: false });

      // Cleanup
      return () => {
        container.removeEventListener("mousedown", onMouseDown);
        container.removeEventListener("mouseleave", onMouseLeave);
        container.removeEventListener("mouseup", onMouseUp);
        container.removeEventListener("mousemove", onMouseMove);
        container.removeEventListener("touchstart", onTouchStart);
        container.removeEventListener("touchend", onTouchEnd);
        container.removeEventListener("touchmove", onTouchMove);
      };
    }

    // Only enable drag scroll for both areas
    const containers = document.querySelectorAll(".scroll-x-animate");
    const cleanups = [];
    containers.forEach((container) => {
      cleanups.push(enableDragScroll(container));
    });
    return () => {
      cleanups.forEach((cleanup) => cleanup && cleanup());
    };
  }, []);

  // Import videos (assuming you have these files in src/videos)
  const videoFiles = [
    require("../videos/video1.mp4"),
    require("../videos/video2.mp4"),
    require("../videos/video3.mp4"),
    require("../videos/video4.mp4"),
    require("../videos/video5.mp4"),
  ];

  // Example gallery data for web projects (replace with your own images)
  const webProjectGalleries = [
    {
      cover: require("../videos/web1.png"),
      images: [
        require("../videos/web1.png"),
        require("../videos/web1-1.png"),
        require("../videos/web1-2.png"),
      ],
      title: "Inventovate",
    },
    {
      cover: require("../videos/web2.png"),
      images: [
        require("../videos/web2.png"),
        require("../videos/web2-1.png"),
        require("../videos/web2-2.png"),
      ],
      title: "Geographical Data Dashboard",
    },
    {
      cover: require("../videos/web3.png"),
      images: [
        require("../videos/web3.png"),
        require("../videos/web3-1.png"),
        require("../videos/web3-2.png"),
        require("../videos/web3-3.png"),
        require("../videos/web3-4.png"),
      ],
      title: "NAT Data Dashboard",
    },
    {
      cover: require("../videos/web4.png"),
      images: [
        require("../videos/web4.png"),
        require("../videos/web4-1.png"),
        require("../videos/web4-2.png"),
        require("../videos/web4-3.png"),
      ],
      title: "Stroke Prediction Model",
    },
    // Add more projects as needed
  ];

  // Modal state
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalImages, setModalImages] = React.useState([]);
  const [modalIndex, setModalIndex] = React.useState(0);

  function openModal(images, index = 0) {
    setModalImages(images);
    setModalIndex(index);
    setModalOpen(true);
  }
  function closeModal() {
    setModalOpen(false);
  }
  function prevImage() {
    setModalIndex((i) => (i - 1 + modalImages.length) % modalImages.length);
  }
  function nextImage() {
    setModalIndex((i) => (i + 1) % modalImages.length);
  }

  // Close modal on ESC / navigate with arrow keys
  React.useEffect(() => {
    if (!modalOpen) return;
    function onKey(e) {
      if (e.key === "Escape") setModalOpen(false);
      if (e.key === "ArrowLeft") {
        setModalIndex((i) => (i - 1 + modalImages.length) % modalImages.length);
      }
      if (e.key === "ArrowRight") {
        setModalIndex((i) => (i + 1) % modalImages.length);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, modalImages.length]);

  // Lock background scroll while the modal is open, so the page behind
  // can't be scrolled independently of the modal
  React.useEffect(() => {
    if (!modalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [modalOpen]);

  // Add descriptions for each project
  const webProjectDescriptions = [
    "Inventovate is an inventory management system designed for hardware stores to streamline operations, track assets, and enhance productivity through real-time data insights.",
    "Geographical Data Dashboard visualizes the distribution of dengue cases in the Philippines through complex geospatial datasets, providing interactive maps, analytics, and reporting tools for data-driven decisions.",
    "NAT Data Dashboard is a comprehensive platform for visualizing and analyzing data from the National Achievement Test in the Philippines, offering insights into various scientific research areas through interactive charts and reports.",
    "Stroke Prediction Model is a web application that predicts the likelihood of stroke occurrence based on user inputs, utilizing machine learning algorithms to provide personalized health insights and recommendations.",
    // Add more descriptions as needed, matching the order of webProjectGalleries
  ];

  // Projects without screenshots yet — shown as text-only cards
  const otherProjects = [
    {
      title: "MSU-IIT AI Chatbot Integration (Thesis)",
      description:
        "Co-developed and evaluated an AI chatbot integrated into the MSU-IIT website to improve the experience for students and visitors, working across chatbot logic, website integration, and usability testing. Thesis: \u201CEnhancing User Experience on Educational Institution Website: The Integration of AI Chatbot at MSU-IIT Website.\u201D",
      tags: ["Rasa", "React", "Thesis", "Group Project"],
      image: thesisAlabImg,
    },
    {
      title: "QueueMe: A Digital Queue Management System",
      description:
        "Developed a digital queue management system to streamline customer service processes, allowing businesses to manage queues efficiently and reduce wait times through a user-friendly interface. The system includes features such as ticket generation, real-time queue updates, and analytics for service optimization. ",
      tags: ["MongoDB", "React", "Geofence ", "Queue Management", "Innovation"],
      image: queueMeImg,
    },
  ];

  // State for web project carousel
  const [webProjectIndex, setWebProjectIndex] = React.useState(0);

  function prevWebProject() {
    setWebProjectIndex(
      (i) => (i - 1 + webProjectGalleries.length) % webProjectGalleries.length,
    );
  }
  function nextWebProject() {
    setWebProjectIndex((i) => (i + 1) % webProjectGalleries.length);
  }

  return (
    <section
      id="works"
      ref={sectionRef}
      className={"projects section reveal" + (sectionInView ? " in-view" : "")}
    >
      <p className="eyebrow">04 — Projects</p>

      <h2 className="subsection-title">Web Projects</h2>
      <div className="carousel-row">
        <button
          onClick={prevWebProject}
          className="carousel-arrow"
          aria-label="Previous Project"
        >
          &#8592;
        </button>
        <div
          className="project-gallery-box"
          onClick={() =>
            openModal(webProjectGalleries[webProjectIndex].images, 0)
          }
          title={webProjectGalleries[webProjectIndex].title}
        >
          <img
            src={webProjectGalleries[webProjectIndex].cover}
            alt={webProjectGalleries[webProjectIndex].title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div className="gallery-caption">
            {webProjectGalleries[webProjectIndex].title}
          </div>
        </div>
        <button
          onClick={nextWebProject}
          className="carousel-arrow"
          aria-label="Next Project"
        >
          &#8594;
        </button>
      </div>
      <div className="project-caption">
        <p style={{ fontWeight: "bold", fontSize: "20px" }}>
          {webProjectGalleries[webProjectIndex].title}
        </p>
        <p style={{ color: "var(--paper-dim)" }}>
          {webProjectDescriptions[webProjectIndex]}
        </p>
        <span style={{ color: "#888" }}>
          {webProjectIndex + 1} / {webProjectGalleries.length}
        </span>
      </div>
      <h2 className="subsection-title">Other Projects</h2>
      <div className="other-projects-grid">
        {otherProjects.map((proj, i) => (
          <div className="other-project-card" key={i}>
            {proj.image && (
              <img
                src={proj.image}
                alt={proj.title}
                className="other-project-image"
              />
            )}
            <h3>{proj.title}</h3>
            <p>{proj.description}</p>
            <div className="other-project-tags">
              {proj.tags.map((tag, j) => (
                <span className="chip" key={j}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <h2 className="subsection-title">Video Works</h2>
      <div className="scroll-x-animate video-works video-track">
        {duplicateChildren(
          videoFiles.map((src, i) => (
            <div key={i} className="project-video-item" style={{ "--i": i }}>
              <video
                src={src}
                width="519"
                height="294"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                controls
                loop
                muted
                preload="metadata"
              />
            </div>
          )),
          2,
        )}
      </div>
      <p className="project-more">More projects coming soon!</p>
      {/* Modal — rendered via portal so it centers on the real viewport,
          not the (transformed) section it's defined in */}
      {modalOpen &&
        ReactDOM.createPortal(
          <div className="project-modal-overlay" onClick={closeModal}>
            <div
              className="project-modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={modalImages[modalIndex]}
                  alt={`Gallery ${modalIndex + 1}`}
                  style={{
                    maxWidth: "60vw",
                    maxHeight: "60vh",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    background: "#fff",
                  }}
                />
                {/* Navigation buttons below the image */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    alignItems: "center",
                    marginTop: "8px",
                  }}
                >
                  <button
                    onClick={prevImage}
                    style={{
                      fontSize: "24px",
                      background: "none",
                      border: "none",
                      color: "#fff",
                      cursor: "pointer",
                      padding: "8px",
                    }}
                    aria-label="Previous"
                  >
                    &#8592;
                  </button>
                  <span style={{ color: "#fff" }}>
                    {modalIndex + 1} / {modalImages.length}
                  </span>
                  <button
                    onClick={nextImage}
                    style={{
                      fontSize: "24px",
                      background: "none",
                      border: "none",
                      color: "#fff",
                      cursor: "pointer",
                      padding: "8px",
                    }}
                    aria-label="Next"
                  >
                    &#8594;
                  </button>
                </div>
              </div>
              {/* Description on the right */}
              <div
                style={{
                  color: "#fff",
                  maxWidth: "300px",
                  marginLeft: "16px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {(() => {
                  // Find which project this image belongs to
                  const projectIndex = webProjectGalleries.findIndex(
                    (project) =>
                      project.images.includes(modalImages[modalIndex]),
                  );
                  const project = webProjectGalleries[projectIndex];
                  const description =
                    webProjectDescriptions[projectIndex] ||
                    "This is a description for the selected project image. You can customize this text to provide more details about the project, its features, or any other relevant information.";
                  return (
                    <>
                      <h3 style={{ margin: "0 0 12px 0" }}>
                        {project?.title || "Project"}
                      </h3>
                      <p>{description}</p>
                    </>
                  );
                })()}
              </div>
              <button
                onClick={closeModal}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  background: "none",
                  border: "none",
                  color: "#fff",
                  fontSize: "32px",
                  cursor: "pointer",
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}

export default Projects;
